import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Breadcrumb } from '../../../../models/session.model';

@Component({
  selector: 'app-session-events-panel',
  standalone: true,
  imports: [CommonModule],
  host: {
    class: 'flex-1 flex flex-col min-h-0 overflow-hidden'
  },
  template: `
    <div class="flex-1 overflow-y-auto p-4 custom-scrollbar relative flex flex-col h-full" #scrollContainer (wheel)="onUserInteraction()" (touchmove)="onUserInteraction()">
      <!-- Filter -->
      <div class="flex gap-2 flex-wrap mb-4 flex-shrink-0">
        <button *ngFor="let t of categories"
                (click)="toggleFilter(t)"
                class="px-2 py-1 rounded-none text-[10px] font-bold uppercase tracking-wider transition-all"
                [class]="filter.has(t) ? 'bg-zaxis-accent/20 text-zaxis-glow border border-zaxis-accent/40' : 'bg-white/5 text-gray-500 border border-zaxis-border hover:bg-white/10'">
          {{ t }}
        </button>
      </div>

      <div *ngIf="filtered.length === 0" class="text-center py-12 text-gray-500 text-sm">
        No breadcrumbs disponibles.
      </div>

      <!-- Timeline -->
      <div class="relative flex-1">
        <div class="absolute left-[9px] top-0 bottom-0 w-px bg-white/10"></div>
        <div *ngFor="let b of filtered; let i = index" class="relative pl-7 pb-4 group" [id]="'breadcrumb-' + i">
          <div class="absolute left-0 top-1 w-[18px] h-[18px] rounded-none border-2 flex items-center justify-center text-[10px]"
               [class]="getColor(b.category)">
            <i [class]="getIcon(b.category)"></i>
          </div>
          <div class="rounded-none p-3 transition-all duration-300 border"
               [ngClass]="getActiveClasses(b.category, i === activeIndex)">
            <div class="flex items-center justify-between mb-1">
              <span class="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                    [class]="getBadge(b.category)">
                {{ b.category }}
              </span>
              <span class="text-[10px] text-gray-500 font-mono">{{ formatTimestamp(b.timestamp) }}</span>
            </div>
            <p class="text-sm text-gray-300 mt-1">{{ b.message }}</p>
            <div *ngIf="b.exception" class="mt-2">
              <button (click)="viewException.emit(b)" class="text-[10px] bg-red-500/20 text-red-400 px-3 py-1 rounded-none hover:bg-red-500/30 transition-colors font-bold uppercase tracking-wider flex items-center gap-1 border border-red-500/30">
                <i class="ph ph-magnifying-glass"></i> View Stacktrace
              </button>
            </div>
            <details *ngIf="b.data" class="mt-2">
              <summary class="text-[10px] text-gray-500 cursor-pointer hover:text-gray-300 transition-colors">data</summary>
              <pre class="text-[10px] text-gray-500 mt-1 bg-black border border-zaxis-border p-2 rounded overflow-x-auto">{{ b.data | json }}</pre>
            </details>
          </div>
        </div>
      </div>
    </div>
  `
})
export class SessionEventsPanelComponent implements OnChanges, AfterViewInit {
  @Input() breadcrumbs: Breadcrumb[] = [];
  @Input() currentAbsoluteTime: number = 0;
  
  @Output() viewException = new EventEmitter<Breadcrumb>();

  @ViewChild('scrollContainer') scrollContainer?: ElementRef;

  categories: string[] = [];
  filter = new Set<string>();
  filtered: Breadcrumb[] = [];
  activeIndex: number = -1;
  userScrolled = false;
  scrollTimeout: any = null;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['breadcrumbs']) {
      this.updateCategories();
      this.updateFiltered();
    }
    if (changes['currentAbsoluteTime'] || changes['breadcrumbs']) {
      this.updateActiveIndex();
    }
  }

  ngAfterViewInit() {
    if (this.activeIndex >= 0) {
      this.scrollToActive();
    }
  }

  updateCategories() {
    const cats = new Set<string>();
    this.breadcrumbs.forEach(b => cats.add(b.category || 'unknown'));
    const newCats = Array.from(cats);
    
    // Auto-select new categories if they appear
    newCats.forEach(c => {
      if (!this.categories.includes(c)) {
        this.filter.add(c);
      }
    });
    this.categories = newCats;
  }

  updateFiltered() {
    this.filtered = this.breadcrumbs.filter(b => this.filter.has(b.category || 'unknown'));
    this.updateActiveIndex();
  }

  toggleFilter(type: string) {
    if (this.filter.has(type)) {
      this.filter.delete(type);
    } else {
      this.filter.add(type);
    }
    this.updateFiltered();
  }

  updateActiveIndex() {
    if (this.filtered.length === 0) {
      this.activeIndex = -1;
      return;
    }
    
    let newIndex = -1;
    for (let i = this.filtered.length - 1; i >= 0; i--) {
      if (this.filtered[i].timestamp <= this.currentAbsoluteTime) {
        newIndex = i;
        break;
      }
    }

    if (newIndex !== -1 && newIndex !== this.activeIndex) {
      this.activeIndex = newIndex;
      if (!this.userScrolled) {
        this.scrollToActive();
      }
    }
  }

  onUserInteraction() {
    this.userScrolled = true;
    if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      this.userScrolled = false;
      // When auto-scroll resumes, immediately catch up to the current active item
      this.scrollToActive();
    }, 5000); // Resume auto-scroll after 5s of inactivity
  }

  scrollToActive() {
    if (this.activeIndex < 0 || !this.scrollContainer) return;
    const container = this.scrollContainer.nativeElement as HTMLElement;
    setTimeout(() => {
      const el = container.querySelector('#breadcrumb-' + this.activeIndex) as HTMLElement;
      if (el) {
        const targetScroll = el.offsetTop - (container.clientHeight / 2) + (el.clientHeight / 2);
        container.scrollTo({
          top: targetScroll,
          behavior: 'smooth'
        });
      }
    }, 50);
  }

  formatTimestamp(ts: number): string {
    const date = new Date(ts);
    const ms = date.getMilliseconds().toString().padStart(3, '0');
    return `${date.toLocaleTimeString('es-ES', { hour12: false })}.${ms}`;
  }

  getColor(category: string): string {
    const map: Record<string, string> = {
      'Log': 'border-gray-400 bg-gray-400/20',
      'Interaction': 'border-green-400 bg-green-400/20',
      'Warning': 'border-yellow-400 bg-yellow-400/20',
      'Error': 'border-orange-400 bg-orange-400/20',
      'Exception': 'border-orange-400 bg-orange-400/20',
      'Crash': 'border-red-500 bg-red-500/20',
      'crash': 'border-red-500 bg-red-500/20',
    };
    return map[category] || 'border-blue-400 bg-blue-400/20';
  }

  getIcon(category: string): string {
    const map: Record<string, string> = {
      'Log': 'ph ph-terminal-window',
      'Interaction': 'ph ph-hand-pointing',
      'Warning': 'ph ph-warning',
      'Error': 'ph ph-x-circle',
      'Exception': 'ph ph-bug',
      'Crash': 'ph ph-skull',
      'crash': 'ph ph-skull',
    };
    return map[category] || 'ph ph-info';
  }

  getActiveClasses(category: string, isActive: boolean): string {
    if (!isActive) {
      if (category === 'Crash' || category === 'crash') {
        return 'bg-red-900/20 border-red-500/50 text-gray-200 opacity-100';
      }
      if (category === 'Error' || category === 'Exception') {
        return 'bg-orange-900/20 border-orange-500/30 text-gray-200 opacity-90';
      }
      if (category === 'Warning') {
        return 'bg-yellow-900/20 border-yellow-500/30 text-gray-200 opacity-90';
      }
      return 'bg-black border border-zaxis-border hover:border-zaxis-border text-gray-300 opacity-70';
    }
    const map: Record<string, string> = {
      'Log': 'bg-gray-400/10 border-gray-400 shadow-[0_0_15px_rgba(156,163,175,0.3)] text-white scale-[1.02] z-10',
      'Interaction': 'bg-green-400/10 border-green-400 shadow-[0_0_15px_rgba(74,222,128,0.3)] text-white scale-[1.02] z-10',
      'Warning': 'bg-yellow-400/10 border-yellow-400 shadow-[0_0_15px_rgba(250,204,21,0.3)] text-white scale-[1.02] z-10',
      'Error': 'bg-orange-500/20 border-orange-500 shadow-[0_0_15px_rgba(249,115,22,0.4)] text-white scale-[1.02] z-10',
      'Exception': 'bg-orange-500/20 border-orange-500 shadow-[0_0_15px_rgba(249,115,22,0.4)] text-white scale-[1.02] z-10',
      'Crash': 'bg-red-500/30 border-red-500 shadow-[0_0_20px_rgba(239,68,68,0.5)] text-white scale-[1.02] z-10',
      'crash': 'bg-red-500/30 border-red-500 shadow-[0_0_20px_rgba(239,68,68,0.5)] text-white scale-[1.02] z-10',
    };
    return map[category] || 'bg-blue-400/10 border-blue-400 shadow-[0_0_15px_rgba(96,165,250,0.3)] text-white scale-[1.02] z-10';
  }

  getBadge(category: string): string {
    const map: Record<string, string> = {
      'Log': 'bg-gray-500/20 text-gray-400',
      'Interaction': 'bg-green-500/20 text-green-400',
      'Warning': 'bg-yellow-500/20 text-yellow-400',
      'Error': 'bg-orange-500/20 text-orange-400',
      'Exception': 'bg-orange-500/20 text-orange-400',
      'Crash': 'bg-red-500/20 text-red-400 border border-red-500/50',
      'crash': 'bg-red-500/20 text-red-400 border border-red-500/50',
    };
    return map[category] || 'bg-blue-500/20 text-blue-400';
  }
}
